import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Button } from '@/components/ui/button';
import { useApi, useAsync, useAuth } from '@/lib/legacy';
import { Grid as GridJs } from '../../../../ui/components/Grid';
import { LocationDialog as LocationDialogJs } from '../../../SubmitApplicationPage/LocationDialog';
import { useAdjustmentForm as useAdjustmentFormJs } from '../../context/QuoteAdjustment/AdjustmentFormContext';
import { useAdjustmentState as useAdjustmentStateJs } from '../../context/QuoteAdjustment/AdjustmentContext';
import { calculateCoverageLimits, exportToCsv, getDefaultCoverages, validateCSVData } from '../../../../utils';
import { ImportModal as ImportModalJs } from '../../../../components/ImportModal';
import getCoverageColDef from '../../../../utils/getCoverageColDef';
import ResetQuoteModalJs from '../../../../components/ResetQuoteModal';

/* The grid wrapper, the location and import dialogs and the reset modal stay legacy (D6, D18). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: ui/components/Grid
const Grid = GridJs as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: SubmitApplicationPage/LocationDialog.jsx
const LocationDialog = LocationDialogJs as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: components/ImportModal.jsx
const ImportModal = ImportModalJs as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: components/ResetQuoteModal.jsx
const ResetQuoteModal = ResetQuoteModalJs as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: QuoteAdjustment/AdjustmentContext.jsx
const useAdjustmentState = useAdjustmentStateJs as any as () => { adjusting?: boolean };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: QuoteAdjustment/AdjustmentFormContext.jsx
const useAdjustmentForm = useAdjustmentFormJs as any as () => { setValue: (path: string, value: unknown) => void };

const FILL_HANDLE_CELL_SELECTION = { handle: { mode: 'fill' } };

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: the quote or policy and its locations, as the API returns them
type Row = Record<string, any>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: the quote and policy tab views pass their own props through
type CoveragesProps = Record<string, any>;

const defaultReinsurance = {
  maxTotalInsuredValue: 5000000,
  autoFacRate: 0,
  autoFacPremium: 0,
  additionalFacPremium: 0,
  additionalFacOnly: false,
  additionalFacAlreadyGrossed: true,
  excluded: true,
};
const defaultRequirements = { swapNeeded: false, dotNeeded: false };

const dataTypeConversion = (value: unknown) => {
  if (typeof value === 'string' && (value.includes('%') || value.includes('$'))) {
    return parseFloat(value.replace(/\$|,/g, '').replace(/%/g, ''));
  }
  return parseFloat(value as string) ?? 0;
};

/** The coverage grid: read-only on a policy, editable while a quote is being adjusted. */
export const Coverages = forwardRef<unknown, CoveragesProps>((props) => {
  const auth = useAuth();
  const { adjusting } = useAdjustmentState();
  const { setValue } = useAdjustmentForm();
  const [selectedFileRows, setSelectedFileRows] = useState<Row[]>([]);
  const { addOrUpdateCustomerLocation } = useApi();
  const [errors, setErrors] = useState<unknown[]>([]);
  const canEdit = auth && (auth.user?.isUnderwriter || auth.user?.isPolicyAnalyst || auth.user?.isAdmin);
  const {
    quotes,
    setGridReference,
    onSelectionChanged,
    modalOpen,
    setModalOpen,
    setQuoteData,
    importModal,
    setImportModal,
    gridReference,
    setExportHandler,
  } = props;
  const [locationData, setLocationData] = useState<Row | Row[]>({});
  const [mode, setMode] = useState('CREATE');
  const [colDefs, setColDefs] = useState<unknown[]>([]);
  const floodPayoutLimit = useRef(null);
  const earthquakePayoutLimit = useRef(null);

  const data = quotes && quotes.locations ? quotes.locations : [];

  const isEditable = () => adjusting;

  const handleUpdateLocation = useCallback(
    async ({
      id,
      location: locationIn,
      index,
      shouldResetLocation,
    }: {
      id: unknown;
      location: Row;
      index?: number;
      shouldResetLocation?: boolean;
    }) => {
      const newLocation = await addOrUpdateCustomerLocation(id, locationIn, shouldResetLocation);

      if (index) {
        newLocation['coverages'] = getDefaultCoverages(quotes, newLocation);
        newLocation['reinsurance'] = {
          ...defaultReinsurance,
          ...newLocation.reinsurance,
          locationId: newLocation.id,
          maxTotalInsuredValue: newLocation.coveredValue * 1.25,
        };
        newLocation['requirements'] = { ...defaultRequirements, ...newLocation.requirements, locationId: newLocation.id };
        setQuoteData((pre: Row) => ({ ...pre, locations: [...pre.locations, newLocation] }));
        setValue(`quote.locations.${index}`, newLocation);
      } else {
        newLocation['reinsurance'] = { ...newLocation.reinsurance, maxTotalInsuredValue: newLocation.coveredValue * 1.25 };
        const locationIndex = quotes.locations.findIndex((location: Row) => location.riskId === newLocation.riskId || location.id === newLocation.id);
        newLocation.coverages.forEach((coverage: Row) => {
          coverage.locationId = newLocation.id;
        });
        setQuoteData((prev: Row) => {
          const updatedLocations = [...prev.locations];
          updatedLocations[locationIndex] = newLocation;
          return { ...prev, locations: updatedLocations };
        });
        setValue(`quote.locations.${locationIndex}`, newLocation);
      }
      setModalOpen(false);
      setMode('CREATE');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addOrUpdateCustomerLocation, quotes]
  );

  const { execute: executeUpdate, status: updateStatus } = useAsync(handleUpdateLocation, { immediate: false });

  const handleSubmit = (submitted: Row, e: { preventDefault?: () => void } | undefined, shouldResetLocation: boolean) => {
    if (mode === 'CREATE') {
      e?.preventDefault?.();
      const index = quotes.locations.length;
      executeUpdate({ id: quotes.id, location: submitted, index, shouldResetLocation });
    } else {
      const updatedLocations = quotes.locations.map((location: Row) => {
        if (location.id === submitted.id) {
          const oldCoveredValue = location.coveredValue;
          const updatedLocation = { ...location, ...submitted };

          // Only update coverages and their payoutLimit if coveredValue is changed
          if (submitted.coveredValue !== oldCoveredValue) {
            const coverages = getDefaultCoverages(quotes, submitted);
            const riskId = location.riskId;
            const locationIndex = quotes.locations.findIndex((l: Row) => l.riskId === riskId || l.id === submitted.id);

            if (!updatedLocation.reinsurance) updatedLocation.reinsurance = {};
            updatedLocation.reinsurance.maxTotalInsuredValue = updatedLocation.coveredValue * 1.25;
            updatedLocation.coverages = coverages.map((coverage: Row, coverageIndex: number) => {
              const updatedCoverage = {
                ...coverage,
                payoutLimit: calculateCoverageLimits(
                  coverage.coverageType,
                  submitted.coveredValue,
                  submitted.requirements?.floodSublimit ?? 0,
                  submitted.requirements?.earthquakeSublimit ?? 0
                ),
              };
              if (adjusting) setValue(`quote.locations.${locationIndex}.coverages.${coverageIndex}.payoutLimit`, updatedCoverage.payoutLimit);
              return updatedCoverage;
            });
          }
          executeUpdate({ id: quotes.id, location: updatedLocation, shouldResetLocation });

          return updatedLocation;
        }
        return location;
      });
      setQuoteData((pre: Row) => ({ ...pre, locations: updatedLocations }));
      if (adjusting) setValue(`quote.locations`, updatedLocations);
    }
    setLocationData([]);
    setMode('CREATE');
  };

  const validateData = (rows: Row[]) => {
    setErrors(validateCSVData(colDefs, rows));
  };

  const addImportedFileRows = () => {
    const updatedQuoteData = { ...quotes };
    const mappedData = selectedFileRows.map((item) => {
      const id = uuid();
      if (!item) {
        return null;
      }
      let coverageCount = 0;
      for (const key in item) {
        if (key.startsWith('Coverage_')) {
          coverageCount++;
        }
      }
      const allCoverages = ['comprehensive', 'flood', 'earthquake', 'hail', 'collision', 'falsePretense', 'terrorism'];
      return {
        id,
        address: item['Address'] ?? '',
        country: item['Country'] ?? '',
        coverages: [
          ...(item['Coverage']
            ? [
                {
                  aggregate: dataTypeConversion(item['Aggregate Deductible']),
                  deductible: dataTypeConversion(item[`Deductible`]),
                  coverageType: allCoverages[0] ?? '',
                  isIncluded: item[`IsIncluded_0`],
                  locationId: id,
                  netPremium: dataTypeConversion(item['Premium']),
                  payoutLimit: dataTypeConversion(item['Payout Limit']),
                },
              ]
            : []),
          ...Array.from({ length: coverageCount }, (_, i) => ({
            aggregate: dataTypeConversion(item[`Aggregate Deductible_${i + 1}`]),
            deductible: dataTypeConversion(item[`Deductible_${i + 1}`]),
            coverageId: item[`CoverageId_${i + 1}`],
            coverageType: allCoverages[i + 1] ?? '',
            isIncluded: item[`IsIncluded_${i + 1}`],
            locationId: id,
            netPremium: dataTypeConversion(item[`Premium_${i + 1}`]),
            payoutLimit: dataTypeConversion(item[`Payout Limit_${i + 1}`]),
          })),
        ],
        details: {
          crimeZone: item['Crime Index'] ?? '',
          earthquake: item['Earthquake Zone'] ?? '',
        },
        name: item['Name'] ?? '',
      };
    });

    updatedQuoteData.locations = [...updatedQuoteData.locations, ...mappedData];
    if (adjusting) setValue('quote.locations', updatedQuoteData.locations);
    setQuoteData(() => updatedQuoteData);
  };

  useEffect(() => {
    setColDefs(
      getCoverageColDef({
        isEditable,
        setValue,
        quotes,
        adjusting,
        setModalOpen,
        setMode,
        setLocationData,
        floodPayoutLimit,
        earthquakePayoutLimit,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotes]);

  useEffect(() => {
    setExportHandler(() => exportToCsv(gridReference));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridReference, data]);

  return (
    <div>
      <Grid
        data={data}
        columns={colDefs}
        setGridReference={setGridReference}
        rowSelection="multiple"
        onSelectionChanged={onSelectionChanged}
        setQuoteData={setQuoteData}
        floatingFilter={false}
        shouldShowPagination={false}
        cellSelection={FILL_HANDLE_CELL_SELECTION}
      />
      {window.location.pathname.includes('quotes') ? (
        <LocationDialog
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setLocationData([]);
            setMode('CREATE');
          }}
          onSubmit={handleSubmit}
          canEdit={canEdit}
          mode={mode}
          product={quotes.product}
          data={locationData}
          shouldAddRiskId={true}
          locations={quotes.locations}
          processing={updateStatus === 'pending'}
          quoteId={quotes.id}
          saveButtonLabel="Attach Location to Quote"
        />
      ) : null}
      {adjusting ? (
        <div className="flex flex-row justify-between">
          <div className="flex h-[70px] flex-wrap items-start justify-center bg-white">
            <Button variant="outline" className="ml-4 px-4" onClick={() => setModalOpen(true)}>
              Add Location
            </Button>
          </div>
          <ResetQuoteModal quoteId={quotes.id} />
        </div>
      ) : null}
      <ImportModal
        importModal={importModal}
        setImportModal={setImportModal}
        setSelectedFileRows={(rows: Row[]) => {
          setSelectedFileRows(rows);
          validateData(rows);
        }}
        selectedFileRows={selectedFileRows}
        errors={errors}
        setErrors={setErrors}
        addImportedFileRows={addImportedFileRows}
      />
    </div>
  );
});
Coverages.displayName = 'Coverages';
